import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';

const API = '/api/v1/aif/auth';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('aif_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const saveToken = useCallback((tokenData) => {
    localStorage.setItem('aif_token', tokenData.access_token);
    localStorage.setItem('aif_tenant_id', tokenData.tenant_id);
    localStorage.setItem('aif_user_id', tokenData.user_id);
    setToken(tokenData.access_token);
  }, []);

  const fetchUser = useCallback(async () => {
    const stored = localStorage.getItem('aif_token');
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/me`, {
        headers: { Authorization: `Bearer ${stored}` },
      });
      if (!res.ok) throw new Error('Unauthorized');
      const data = await res.json();
      setUser(data);
      setToken(stored);
    } catch {
      localStorage.removeItem('aif_token');
      localStorage.removeItem('aif_tenant_id');
      localStorage.removeItem('aif_user_id');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const res = await apiClient.post('/api/v1/aif/auth/login', { email, password }, { queueOnOffline: false });
      saveToken(res.data || res);
      await fetchUser();
      return res;
    } catch (err) {
      const msg = err.body?.detail || err.message || 'Login failed';
      setError(msg);
      throw err;
    }
  }, [saveToken, fetchUser]);

  const register = useCallback(async ({ email, password, name, farmName, countryCode, regionCode }) => {
    setError(null);
    try {
      const res = await apiClient.post('/api/v1/aif/auth/register', {
        email,
        password,
        name,
        farm_name: farmName || undefined,
        country_code: countryCode || 'DE',
        region_code: regionCode || undefined,
        language: 'de',
      }, { queueOnOffline: false });
      saveToken(res.data || res);
      await fetchUser();
      return res;
    } catch (err) {
      const msg = err.body?.detail || err.message || 'Registration failed';
      setError(msg);
      throw err;
    }
  }, [saveToken, fetchUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('aif_token');
    localStorage.removeItem('aif_tenant_id');
    localStorage.removeItem('aif_user_id');
    setToken(null);
    setUser(null);
  }, []);

  return { user, token, loading, error, login, register, logout };
}
